/**
 * Arabic for HTML nobody can wrap.
 *
 * The ported boards are generated from the original demo (`demo/domain/
 * generated.ts`, rebuilt by regen.sh and pinned by tests) and write their
 * panes as HTML strings — thousands of labels no `t()` can reach without
 * rewriting the source they are generated from. So inside an element marked
 * `data-i18n-dom`, while Arabic is chosen, a text node whose whole text is a
 * dictionary key is replaced by its Arabic, and so are `title`,
 * `placeholder` and `aria-label`.
 *
 * Only whole matches are replaced. A reading ("97.4 %"), a code ("RWY 17/35"),
 * a name or anything else not in the dictionary is left exactly as written, so
 * nothing is half-translated and no figure is touched. English readers get no
 * observer at all.
 */
import { AR, getLang } from ".";

const ARABIC = /[؀-ۿ]/;
const LETTERS = /[A-Za-z]/;
const ATTRS = ["title", "placeholder", "aria-label"] as const;
const SCOPE = "[data-i18n-dom]";

/* The boards set some labels in capitals in the source rather than in CSS. */
const upperIndex = new Map<string, string>();
function lookup(raw: string): string | undefined {
  /* generated HTML carries its template's line breaks and indentation */
  const text = raw.replace(/\s+/g, " ");
  const hit = AR[text];
  if (hit !== undefined) return hit;
  if (upperIndex.size === 0) for (const k of Object.keys(AR)) upperIndex.set(k.toUpperCase(), AR[k]);
  return text === text.toUpperCase() ? upperIndex.get(text) : undefined;
}

function translateText(node: Text) {
  const raw = node.nodeValue;
  if (!raw) return;
  const text = raw.trim();
  if (!text || !LETTERS.test(text) || ARABIC.test(text)) return;
  const ar = lookup(text);
  if (ar === undefined || ar === text) return;
  const next = raw.replace(text, ar);
  /* Writing a node reports a mutation, which calls this again. An entry whose
     Arabic equals its English ("SMS") must never be written, or the page
     translates the same node forever and freezes. */
  if (next !== raw) node.nodeValue = next;
}

function translateElement(el: Element) {
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (!v || ARABIC.test(v) || !LETTERS.test(v)) continue;
    const ar = lookup(v.trim());
    if (ar !== undefined && ar !== v) el.setAttribute(a, ar);
  }
}

function translateTree(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    translateText(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const el = root as Element;
  if (el.closest("input, textarea, script, style, [data-i18n-skip]")) return;
  translateElement(el);
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) translateText(n as Text);
    else translateElement(n as Element);
  }
}

function inScope(node: Node): boolean {
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return !!el?.closest(SCOPE) && !el.closest("[data-i18n-skip]");
}

/**
 * Watch the page while Arabic is chosen. Returns a stop function. Called once
 * by the shell; the language provider remounts the shell on a change of
 * language, so it starts and stops with it.
 */
export function watchDom(root: HTMLElement = document.body): () => void {
  if (getLang() !== "ar" || typeof MutationObserver === "undefined") return () => {};
  root.querySelectorAll(SCOPE).forEach((el) => translateTree(el));
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "characterData") {
        if (inScope(r.target)) translateText(r.target as Text);
      } else if (r.type === "attributes") {
        if (inScope(r.target)) translateElement(r.target as Element);
      } else {
        for (const n of r.addedNodes) {
          if (n.nodeType === Node.ELEMENT_NODE && (n as Element).matches(SCOPE)) translateTree(n);
          else if (inScope(n)) translateTree(n);
        }
      }
    }
  });
  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...ATTRS],
  });
  return () => observer.disconnect();
}
